import { ACTIVITY_REGISTRY } from "@/lib/activities/registry";
import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { useActivityMessage } from "@/lib/hooks/conversations/use-activity-message";
import { CheckCircle2, PlayCircle, Sparkles, XCircle } from "lucide-react";

interface ActivityMessageRendererProps {
  message: {
    text: string;
    customType: string;
    data?: string;
  };
  context?: ChatRenderContext;
}

export function ActivityMessageRenderer({
  message,
  context,
}: ActivityMessageRendererProps) {
  const {
    activityData,
    activity,
    isEnabled,
    teenName,
    mentorName,
    mentorAvatarUrl,
    teenAvatarUrl,
    openInHub,
  } = useActivityMessage(message, { context });

  if (!activityData?.entity_id || !isEnabled) {
    return <ActivityMessageFallback text={message.text} />;
  }

  const module = ACTIVITY_REGISTRY[activityData.activity_type];
  const messageStatus = activityData.status;
  const label = activity?.activityDefinition?.label || "Activity";
  const isAdminSurface = context?.surface === "admin";
  const fallbackText = message.text || "Activity update";

  if (messageStatus === "sent") {
    if (
      activity &&
      activity.status !== "sent" &&
      activity.status !== "in_progress"
    ) {
      return <ActivityMessageFallback text={fallbackText} />;
    }

    if (activity?.isExpired) {
      return (
        <div className="flex items-center justify-center py-3 opacity-40">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-zinc-400" />
            <span className="text-sm text-zinc-400">Activity expired</span>
          </div>
        </div>
      );
    }

    const isInProgress = activity?.status === "in_progress";

    return (
      <div className="flex items-center justify-center py-3">
        <div className="flex items-center gap-2">
          {isInProgress ? (
            <PlayCircle className="h-4 w-4 text-zinc-500" />
          ) : (
            <Sparkles className="h-4 w-4 text-indigo-500" />
          )}
          <span className="text-sm text-zinc-600">
            {isInProgress
              ? getInProgressCaption({ fallbackText, isAdminSurface, teenName, label })
              : getSentCaption({ fallbackText, isAdminSurface, label })}
          </span>
        </div>
      </div>
    );
  }

  if (messageStatus === "in_progress") {
    return (
      <ActivityMessageFallback
        text={getInProgressCaption({ fallbackText, isAdminSurface, teenName, label })}
      />
    );
  }

  if (messageStatus === "completed") {
    const result = activity?.result || activityData.result;
    const completionCaption = getCompletedCaption({
      fallbackText,
      isAdminSurface,
      teenName,
      label,
    });

    return (
      <div className="flex flex-col items-center py-3">
        <div className="mb-2 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-sm text-zinc-600">{completionCaption}</span>
        </div>
        {result && module ? (
          <module.ResultCard
            participants={{
              teen: { name: teenName, avatarUrl: teenAvatarUrl },
              mentor: { name: mentorName, avatarUrl: mentorAvatarUrl },
            }}
            result={result}
            config={activity?.config ?? {}}
            onClick={openInHub}
          />
        ) : (
          <div className="rounded-lg bg-zinc-50 px-4 py-3">
            <span className="text-sm text-zinc-500">Activity completed</span>
          </div>
        )}
      </div>
    );
  }

  if (messageStatus === "declined") {
    return (
      <div className="flex items-center justify-center py-3 opacity-60">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">
            {getDeclinedCaption({ fallbackText, isAdminSurface, teenName, label })}
          </span>
        </div>
      </div>
    );
  }

  return <ActivityMessageFallback text={fallbackText} />;
}

function ActivityMessageFallback({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center py-3">
      <span className="text-sm text-zinc-600">{text}</span>
    </div>
  );
}

function getSentCaption({
  fallbackText,
  isAdminSurface,
  label,
}: {
  fallbackText: string;
  isAdminSurface: boolean;
  label: string;
}) {
  return isAdminSurface ? fallbackText : `You sent a ${label} invite`;
}

function getInProgressCaption({
  fallbackText,
  isAdminSurface,
  teenName,
  label,
}: {
  fallbackText: string;
  isAdminSurface: boolean;
  teenName: string;
  label: string;
}) {
  return isAdminSurface ? fallbackText : `${teenName} is working on ${label}`;
}

function getCompletedCaption({
  fallbackText,
  isAdminSurface,
  teenName,
  label,
}: {
  fallbackText: string;
  isAdminSurface: boolean;
  teenName: string;
  label: string;
}) {
  return isAdminSurface ? fallbackText : `${teenName} completed ${label}`;
}

function getDeclinedCaption({
  fallbackText,
  isAdminSurface,
  teenName,
  label,
}: {
  fallbackText: string;
  isAdminSurface: boolean;
  teenName: string;
  label: string;
}) {
  return isAdminSurface ? fallbackText : `${teenName} declined ${label}`;
}
